import {
    BadgeCheck,
    Bug,
    Crosshair,
    FileCode,
    FileText,
    Fingerprint,
    Globe,
    Link as LinkIcon,
    Mail,
    Network,
    Server,
    Shield,
    Skull,
    Target,
} from "lucide-react";

/**
 * Per-IoC-type visual identity: icon + accent class. The accent maps to one of
 * our token-driven color roles via inline CSS variable names so swapping
 * themes keeps the icons in step. We intentionally don't use background
 * colors — the icon alone (with consistent accent) is enough signal in a
 * dense table without turning the row into rainbow soup.
 */

type Visual = {
    Icon: React.ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean }>;
    accent: string; // CSS var
    label: string;
};

const FALLBACK: Visual = { Icon: Shield, accent: "var(--color-text-muted)", label: "Indicator" };

const MAP: Record<string, Visual> = {
    ip:            { Icon: Server,       accent: "var(--color-info)",          label: "IP" },
    cidr:          { Icon: Network,      accent: "var(--color-info)",          label: "CIDR" },
    domain:        { Icon: Globe,        accent: "var(--color-accent)",        label: "Domain" },
    email:         { Icon: Mail,         accent: "var(--color-success)",       label: "Email" },
    url:           { Icon: LinkIcon,     accent: "var(--color-accent)",        label: "URL" },
    certificate:   { Icon: BadgeCheck,   accent: "var(--color-success)",       label: "Certificate" },
    hash:          { Icon: Fingerprint,  accent: "var(--color-warn)",          label: "Hash" },
    yara:          { Icon: FileCode,     accent: "var(--color-warn)",          label: "YARA" },
    av_signature:  { Icon: Shield,       accent: "var(--color-warn)",          label: "AV signature" },
    cve:           { Icon: Bug,          accent: "var(--color-danger)",        label: "CVE" },
    threat_report: { Icon: FileText,     accent: "var(--color-info)",          label: "Threat report" },
    apt:           { Icon: Skull,        accent: "var(--color-danger)",        label: "APT group" },
    campaign:      { Icon: Target,       accent: "var(--color-danger)",        label: "Campaign" },
    ttp:           { Icon: Crosshair,    accent: "var(--color-danger)",        label: "MITRE TTP" },
};

export function visualFor(type: string): Visual {
    return MAP[type] ?? FALLBACK;
}

interface TypeBadgeProps {
    type: string;
    showLabel?: boolean;
    size?: "sm" | "md";
}

/** Compact icon+label badge used in the IoC table and detail header. */
export function TypeBadge({ type, showLabel = true, size = "sm" }: TypeBadgeProps): React.ReactElement {
    const v = visualFor(type);
    const px = size === "md" ? 18 : 14;
    return (
        <span
            className="cluster-xs"
            style={{
                gap: "var(--space-2)",
                color: v.accent,
                fontSize: size === "md" ? "var(--font-size-13)" : "var(--font-size-12)",
                fontWeight: "var(--font-weight-medium)",
            }}
            title={v.label}
        >
            <v.Icon size={px} aria-hidden />
            {showLabel && <span>{v.label}</span>}
        </span>
    );
}

/** Severity → status-pill class (security severity tokens). */
export function severityPillClass(s: number): string {
    if (s >= 9) return "sev-critical";
    if (s >= 7) return "sev-high";
    if (s >= 4) return "sev-medium";
    if (s >= 2) return "sev-low";
    return "sev-info";
}

export function severityLabel(s: number): string {
    if (s >= 9) return "critical";
    if (s >= 7) return "high";
    if (s >= 4) return "medium";
    if (s >= 2) return "low";
    return "info";
}
